import { defaultValues } from "../../../../services/defaults";
import { inputToTags } from "../../../../mst/kinds/utils";
import { OptimisticConcurrency } from "../../../../schema/types/base";
import { inputToSidecar } from "../../../../mst/stores/cpln.jwt.client.draft";
import jsYaml from "js-yaml";
import { WorkloadCreate } from "../../../../schema/types/workload/create";
import {
  ContainerSpec,
  FirewallSpec,
  HealthCheckSpec,
  InternalInboundAllowType,
  LoadBalancer,
  LoadBalancerPort,
  RolloutOptions,
  WorkloadType,
} from "../../../../schema/types/workload/entity";
import {
  ContainerInput,
  FirewallInput,
  LoadBalancerInput,
  OptionsInputBase,
  OptionsMetricInput,
  ProbeInput,
  RolloutOptionsInput,
  WorkloadInput,
} from "../../../../schema/types/workload/input";
import { DefaultOptions, LocalOption } from "../../../../schema/types/workload/workloadOptions";
import { resourceLink } from "../../../../services/cpln";
import { store } from "../../../../store";
import { parseLink } from "../../../../utils/linkParser/linkParser";
import { partsToCpu, partsToMemory } from "../../../volumeset/utils";
import { inputToEnv } from "./envVar";
import { inputToVolume } from "./volume";

function getCapacityAiFromInput(input: WorkloadInput): boolean {
  const capacityAi =
    (input.defaultOptions.alreadySet || input.defaultOptions.override) && input.defaultOptions.capacityAI;
  return capacityAi;
}

function inputToProbe(input: ProbeInput): HealthCheckSpec | undefined {
  if (!input.alreadySet && !input.override) {
    return undefined;
  }

  const p: HealthCheckSpec = {
    failureThreshold: Number(input.failureThreshold),
    successThreshold: Number(input.successThreshold),
    initialDelaySeconds: Number(input.initialDelaySeconds),
    periodSeconds: Number(input.periodSeconds),
    timeoutSeconds: Number(input.timeoutSeconds),
  };

  if (input.type === "exec") {
    p.exec = {
      command: [input.exec.command, ...input.exec.args.map((arg) => arg.value)],
    };
  }

  if (input.type === "grpc") {
    if (input.grpc.port.length > 0) {
      p.grpc = {
        port: Number(input.grpc.port),
      };
    }
  }

  if (input.type === "tcp") {
    if (input.tcpSocket.port.length > 0) {
      p.tcpSocket = {
        port: Number(input.tcpSocket.port),
      };
    }
  }

  if (input.type === "http") {
    p.httpGet = {
      path: "/",
      scheme: input.httpGet.scheme,
    };
    if (input.httpGet.path) {
      p.httpGet!.path = input.httpGet.path;
    }
    if (input.httpGet.port) {
      p.httpGet!.port = Number(input.httpGet.port);
    }
    if (input.httpGet.httpHeaders.length > 0) {
      p.httpGet!.httpHeaders = input.httpGet.httpHeaders.map((header) => ({ name: header.name, value: header.value }));
    }
  }

  return p;
}

export function inputToContainer(input: WorkloadInput, c: ContainerInput): ContainerSpec {
  const capacityAi = getCapacityAiFromInput(input);

  const container: ContainerSpec = {
    name: c.name.value,
    image: c.image.isExternal ? c.image.externalValue : c.image.cplnValue.link,
    inheritEnv: c.inheritEnv,
    cpu: partsToCpu(c.cpu),
    memory: partsToMemory(c.memory),
  };

  if (input.type === "stateful" || capacityAi) {
    if (c.minCpu?.amount) {
      container.minCpu = partsToCpu(c.minCpu);
    }
    if (c.minMemory?.amount) {
      container.minMemory = partsToMemory(c.minMemory);
    }
  }

  container.env = inputToEnv(c.env);
  container.volumes = inputToVolume(c.volumes);

  if (c.gpu.type === "nvidia") {
    container.gpu = {
      nvidia: {
        model: c.gpu.nvidia.model,
        quantity: Number(c.gpu.nvidia.quantity),
      },
    };
  }
  if (c.gpu.type === "custom") {
    container.gpu = {
      custom: {
        resource: c.gpu.custom.resource,
      },
    };
    if (c.gpu.custom.runtimeClass) {
      container.gpu.custom!.runtimeClass = c.gpu.custom.runtimeClass;
    }
    if (c.gpu.custom.quantity) {
      container.gpu.custom!.quantity = Number(c.gpu.custom.quantity);
    }
  }

  if (input.type !== "cron") {
    const readinessProbe = inputToProbe(c.readinessProbe);
    if (readinessProbe) {
      container.readinessProbe = readinessProbe;
    }
    const livenessProbe = inputToProbe(c.livenessProbe);
    if (livenessProbe) {
      container.livenessProbe = livenessProbe;
    }
  }

  if (c.workingDir) {
    container.workingDir = c.workingDir;
  }
  if (c.command) {
    container.command = c.command;
  }
  if (c.args.length > 0) {
    container.args = c.args.map((arg) => arg.value);
  }

  if (c.lifecycle.preStop.exec.isConfigured || c.lifecycle.postStart.exec.isConfigured) {
    container.lifecycle = {};
    if (c.lifecycle.preStop.exec.isConfigured) {
      container.lifecycle.preStop = {
        exec: {
          command: [c.lifecycle.preStop.exec.command, ...c.lifecycle.preStop.exec.args.map((arg) => arg.value)],
        },
      };
    }
    if (c.lifecycle.postStart.exec.isConfigured) {
      container.lifecycle.postStart = {
        exec: {
          command: [c.lifecycle.postStart.exec.command, ...c.lifecycle.postStart.exec.args.map((arg) => arg.value)],
        },
      };
    }
  }

  if (c.metrics.isEnabled) {
    container.metrics = {
      path: c.metrics.path,
      port: Number(c.metrics.port),
    };
    if (c.metrics.dropMetrics.length > 0) {
      container.metrics.dropMetrics = c.metrics.dropMetrics.map((dm) => dm.value);
    }
  }

  if (input.type === "serverless" && c.servesTraffic && c.ports.length > 0) {
    container.ports = [
      {
        protocol: c.ports[0].protocol,
        number: Number(c.ports[0].number),
      },
    ];
  }
  if (input.type !== "serverless") {
    container.ports = c.ports.map((p) => ({ number: Number(p.number), protocol: p.protocol }));
  }

  return container;
}

export function inputToFirewall(i: FirewallInput, selfLink: string): FirewallSpec {
  const f: FirewallSpec = {
    internal: {},
    external: {},
  };

  // internal
  const internalType = i.internal.type === "same-gvc-and-workload-list" ? "same-gvc" : i.internal.type;
  f.internal!.inboundAllowType = internalType;

  const typesWithWorkloadList: InternalInboundAllowType[] = ["same-gvc", "workload-list"];
  if (typesWithWorkloadList.includes(internalType)) {
    const inboundAllowLinks: string[] = i.internal.workloads.map((w) => w.link);
    const agentLinks: string[] = i.internal.agents.map((a) => a.link);
    inboundAllowLinks.push(...agentLinks);
    if (i.internal.allowKedaAccess) {
      inboundAllowLinks.push(`cpln://internal/keda`);
    }
    if (i.internal.allowAccessToItself) {
      inboundAllowLinks.push(selfLink);
    }
    f.internal!.inboundAllowWorkload = inboundAllowLinks;
  }

  // console.log("ItoW 4");
  // TODO make a const
  const publicCidr = "0.0.0.0/0";

  // external
  // inbound
  if (i.external.inbound.public) {
    f.external!.inboundAllowCIDR = [publicCidr];
  } else {
    f.external!.inboundAllowCIDR = i.external.inbound.allowCidr.map((c) => c.value);
  }
  f.external!.inboundBlockedCIDR = i.external.inbound.blockedCidr.map((c) => c.value);

  if (i.external.httpInboundHeaderFilter.length > 0) {
    f.external!.http = {
      inboundHeaderFilter: i.external.httpInboundHeaderFilter.map((f) => {
        if (f.type === "allowed") {
          return {
            key: f.key,
            allowedValues: f.values,
          };
        }

        // assume block
        return {
          key: f.key,
          blockedValues: f.values,
        };
      }),
    };
  }

  // console.log("ItoW 5");
  // outbound
  if (i.external.outbound.public) {
    f.external!.outboundAllowCIDR = [publicCidr];
  } else {
    f.external!.outboundAllowCIDR = i.external.outbound.allowCidr.map((c) => c.value);
    f.external!.outboundAllowHostname = i.external.outbound.allowHostname.map((c) => c.value);
  }
  f.external!.outboundBlockedCIDR = i.external.outbound.blockedCidr.map((c) => c.value);
  f.external!.outboundAllowPort = i.external.outbound.allowPort.map((p) => ({
    number: Number(p.number),
    protocol: p.protocol,
  }));

  return f;
}

export function inputToOptions(i: OptionsInputBase, type: WorkloadType): DefaultOptions {
  let capacityAi = i.capacityAI;
  const defaultOptions: DefaultOptions = {
    capacityAI: capacityAi,
    debug: i.debug,
    suspend: i.suspend,
    multiZone: {
      enabled: i.multiZone.isEnabled,
    },
    autoscaling: {
      minScale:
        i.autoscaling.minScale.length > 0
          ? Number(i.autoscaling.minScale)
          : Number(defaultValues.workload.options.autoscaling.minScale.default),
      maxScale:
        i.autoscaling.maxScale.length > 0
          ? Number(i.autoscaling.maxScale)
          : Number(defaultValues.workload.options.autoscaling.maxScale.default),
      maxConcurrency: Number(i.autoscaling.maxConcurrency),
      scaleToZeroDelay:
        i.autoscaling.scaleToZeroDelay.length > 0
          ? Number(i.autoscaling.scaleToZeroDelay)
          : Number(defaultValues.workload.options.autoscaling.scaleToZeroDelay.default),
    },
  };
  if (i.autoscaling.metric === "disabled") {
    // When metric is disabled and minScale is 0, set maxScale to 1 for serverless so workload can scale up
    const minScale = defaultOptions.autoscaling!.minScale;
    defaultOptions.autoscaling!.maxScale = type === "serverless" && minScale === 0 ? 1 : minScale;
  }

  if (i.timeoutSeconds.length > 0) {
    defaultOptions.timeoutSeconds = Number(i.timeoutSeconds);
  }
  if (capacityAi && i.capacityAIUpdateMinutes.length > 0) {
    defaultOptions.capacityAIUpdateMinutes = Number(i.capacityAIUpdateMinutes);
  }
  if (i.autoscaling!.metric !== "multi") {
    defaultOptions.autoscaling!.metric = i.autoscaling!.metric;
  }
  if (i.autoscaling.metric === "multi") {
    defaultOptions.autoscaling!.multi = [];
    if (i.autoscaling.multi.cpu.length > 0) {
      defaultOptions.autoscaling!.multi.push({
        metric: "cpu",
        target: Number(i.autoscaling.multi.cpu),
      });
    }
    if (i.autoscaling.multi.memory.length > 0) {
      defaultOptions.autoscaling!.multi.push({
        metric: "memory",
        target: Number(i.autoscaling.multi.memory),
      });
    }
    if (i.autoscaling.multi.rps.length > 0) {
      defaultOptions.autoscaling!.multi.push({
        metric: "rps",
        target: Number(i.autoscaling.multi.rps),
      });
    }
  }
  if (i.autoscaling.metric === "latency") {
    defaultOptions.autoscaling!.metricPercentile = i.autoscaling.metricPercentile;
  }
  const metricsWithoutTarget: OptionsMetricInput[] = ["disabled", "keda", "multi"];
  if (!metricsWithoutTarget.includes(i.autoscaling.metric)) {
    defaultOptions.autoscaling!.target =
      i.autoscaling.target.length > 0
        ? Number(i.autoscaling.target)
        : Number(defaultValues.workload.options.autoscaling.target.default);
  }
  if (i.autoscaling.keda) {
    try {
      const parsed = jsYaml.load(i.autoscaling.keda) as any;
      if (i.autoscaling.metric === "keda" || parsed?.triggers?.length >= 1) {
        defaultOptions.autoscaling!.keda = parsed;
      }
    } catch (e) {
      // Intentionally silenced the error
      console.error("keda parse failed", e.message);
    }
  }
  if (type !== "serverless") {
    // @ts-ignore
    delete defaultOptions.autoscaling!.scaleToZeroDelay;
    // @ts-ignore
    delete defaultOptions.autoscaling!.maxConcurrency;
  }
  if (type === "cron") {
    // @ts-ignore
    delete defaultOptions.autoscaling;
  }

  return defaultOptions;
}

export function inputToLoadBalancer(i: LoadBalancerInput, type: WorkloadType): LoadBalancer | undefined {
  const l: LoadBalancer = {};

  if (type === "cron") {
    return undefined;
  }

  if (i.direct.isEnabled) {
    // console.log("ItoW LB 1");
    l.direct = {
      enabled: i.direct.isEnabled,
    };
    if (i.direct.ipSet.link) {
      l.direct!.ipSet = i.direct.ipSet.link;
    }
    // console.log("ItoW LB 2");
    if (i.direct.ports.length > 0) {
      l.direct!.ports = i.direct.ports.map((p) => {
        const port: LoadBalancerPort = { protocol: p.protocol, externalPort: Number(p.externalPort) };
        if (p.containerPort.length > 0) {
          port.containerPort = Number(p.containerPort);
        }
        if (p.scheme.length > 0) {
          port.scheme = p.scheme;
        }
        return port;
      });
    }
  }

  // console.log("ItoW LB 3");
  if (i.geoLocation.isEnabled) {
    l.geoLocation = {
      enabled: true,
      headers: {},
    };
    if (i.geoLocation.headers.asn.length > 0) {
      l.geoLocation.headers!.asn = i.geoLocation.headers.asn;
    }
    if (i.geoLocation.headers.city.length > 0) {
      l.geoLocation.headers!.city = i.geoLocation.headers.city;
    }
    if (i.geoLocation.headers.country.length > 0) {
      l.geoLocation.headers!.country = i.geoLocation.headers.country;
    }
    if (i.geoLocation.headers.region.length > 0) {
      l.geoLocation.headers!.region = i.geoLocation.headers.region;
    }
  }

  // console.log("ItoW LB 4");
  if (type === "stateful" && i.replicaDirect) {
    l.replicaDirect = true;
  }
  return l;
}

export function inputToRolloutOptions(i: RolloutOptionsInput, type: WorkloadType): RolloutOptions | undefined {
  if (type === "cron" || !i.isEnabled) {
    return undefined;
  }

  const r: RolloutOptions = {
    minReadySeconds: Number(i.minReadySeconds),
    scalingPolicy: i.scalingPolicy,
    terminationGracePeriodSeconds:
      i.terminationGracePeriodSeconds.length > 0
        ? Number(i.terminationGracePeriodSeconds)
        : Number(defaultValues.workload.rolloutOptions.terminationGracePeriodSeconds.default),
  };
  if (i.maxSurgeReplicas.length > 0) {
    r.maxSurgeReplicas = i.maxSurgeReplicas;
  }
  if (type !== "stateful" && i.maxUnavailableReplicas.length > 0) {
    r.maxUnavailableReplicas = i.maxUnavailableReplicas;
  }
  return r;
}

export function inputToJob(job: WorkloadInput["job"]) {
  return {
    schedule: job.schedule,
    concurrencyPolicy: job.concurrencyPolicy,
    historyLimit: job.historyLimit,
    restartPolicy: job.restartPolicy,
    activeDeadlineSeconds: job.activeDeadlineSeconds,
  };
}

export function inputToWorkload(input: WorkloadInput): WorkloadCreate {
  // console.log("ItoW 1");
  const org = store.getState().context.org;
  const gvc = parseLink(input.gvc.link, { useInputCtx: true }).name;
  const ctx = { org: org, gvc: gvc };
  const selfLink = resourceLink("workload", input.name, ctx);

  const i = input;

  // console.log("ItoW 2");
  let containers: ContainerSpec[] = [];
  for (const containerInput of i.containers) {
    const container = inputToContainer(input, containerInput);
    containers.push(container);
  }

  const w: WorkloadCreate = {
    kind: "workload",
    name: i.name,
    tags: inputToTags(i.tags),
    spec: {
      containers: containers,
      supportDynamicTags: input.supportDynamicTags,
      type: input.type,
    },
  };
  if (i.description) {
    w.description = i.description;
  }

  // console.log("ItoW 3");
  w.spec.firewallConfig = inputToFirewall(i.firewallConfig, selfLink);

  if (i.identity.value.link) {
    w.spec.identityLink = i.identity.value.link;
  }

  // console.log("ItoW 6");
  if (i.defaultOptions.alreadySet || i.defaultOptions.override) {
    w.spec.defaultOptions = inputToOptions(i.defaultOptions, i.type);
  }

  // console.log("ItoW 7");
  const localOptions: LocalOption[] = [];
  for (const localOptionInput of i.localOptions) {
    const localOptionsDefaultPart = inputToOptions(localOptionInput, i.type);

    const localOption: LocalOption = {
      location: `//location/${localOptionInput.location}`,
      ...localOptionsDefaultPart,
    };
    localOptions.push(localOption);
  }
  w.spec.localOptions = localOptions;

  // console.log("ItoW 8");
  if (i.extras) {
    try {
      w.spec.extras = jsYaml.load(i.extras) as any;
    } catch (e) {
      // Intentionally silenced the error
      console.error("extras yaml parse failed", e.message);
    }
  }

  if (i.type === "cron") {
    w.spec.job = {
      concurrencyPolicy: i.job.concurrencyPolicy,
      historyLimit:
        i.job.historyLimit.length > 0
          ? Number(i.job.historyLimit)
          : Number(defaultValues.workload.job.historyLimit.default),
      restartPolicy: i.job.restartPolicy,
    };
    w.spec.job.schedule = i.job.schedule;
    if (i.job.activeDeadlineSeconds.length > 0) {
      w.spec.job.activeDeadlineSeconds = Number(i.job.activeDeadlineSeconds);
    }
  }

  const rolloutOptions = inputToRolloutOptions(i.rolloutOptions, i.type);
  if (rolloutOptions) {
    w.spec.rolloutOptions = rolloutOptions;
  }

  // console.log("ItoW 9");
  // this check is not a !! check because it is a number, 0 returns falsy
  if (i.securityOptions.filesystemGroupId.length > 0 || i.securityOptions.runAsUser.length > 0) {
    w.spec.securityOptions = {};
    if (i.securityOptions.filesystemGroupId.length > 0) {
      w.spec.securityOptions.filesystemGroupId = Number(i.securityOptions.filesystemGroupId);
    }
    if (i.securityOptions.runAsUser.length > 0) {
      w.spec.securityOptions.runAsUser = Number(i.securityOptions.runAsUser);
    }
  }

  if (i.requestRetryPolicy.attempts.length > 0 || i.requestRetryPolicy.retryOn.length > 0) {
    w.spec.requestRetryPolicy = {
      attempts:
        i.requestRetryPolicy.attempts.length > 0
          ? Number(i.requestRetryPolicy.attempts)
          : Number(defaultValues.workload.requestRetryPolicy.attempts.default),
      retryOn: i.requestRetryPolicy.retryOn,
    };
  }

  const loadBalancer = inputToLoadBalancer(i.loadBalancer, i.type);
  if (loadBalancer) {
    w.spec.loadBalancer = loadBalancer;
  }

  // console.log("ItoW 10");
  const sidecar = inputToSidecar(i.sidecar);
  if (sidecar) {
    w.spec.sidecar = sidecar;
  }

  // console.log("ItoW Done");
  return w;
}

export function workloadToPatch(_workload: WorkloadCreate, concurrency: OptimisticConcurrency) {
  const workload: any = JSON.parse(JSON.stringify(_workload));

  workload[`$replace/spec`] = workload.spec;
  delete workload.spec;

  workload[`$replace/tags`] = workload.tags;
  delete workload.tags;

  workload.id = concurrency.id;
  workload.version = concurrency.version;

  return workload;
}
